import { z } from 'zod';
/**
 * Morning Brief contracts — the personalized daily AI intelligence layer.
 *
 * A brief is a precomputed, per-user, per-day snapshot (built by the 6 AM job)
 * that aggregates ONLY data the user is permitted to see, has the AI narrate
 * those pre-fetched facts, and persists the result so the first-login popup +
 * all-day header drawer/page render instantly. Each section deep-links into the
 * existing Reports module via a "View more" AQL link.
 *
 * DTO conventions (match planning.ts): dates are ISO strings; fields that may be
 * absent are `.nullable()`/`.optional()`; `*Input` schemas are what the API
 * accepts on update.
 */
const isoDate = z.string();
const nString = z.string().nullable().optional();
const nDate = z.string().nullable().optional();
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const MorningBriefStatus = z.enum(['pending', 'generating', 'generated', 'failed']);
/** Which section of the brief an item belongs to. */
export const MorningBriefSection = z.enum([
    'needs_attention',
    'mention_comment',
    'mention_document',
    'assigned_work',
    'watched_update',
    'highlight',
    'lowlight',
    'recommended_focus',
]);
/** The kind of entity an item points at. */
export const MorningBriefItemType = z.enum([
    'ticket',
    'comment',
    'document',
    'page',
    'release',
    'sprint',
    'pr',
    'notification',
    'focus',
]);
export const MorningBriefSeverity = z.enum(['info', 'low', 'medium', 'high', 'critical']);
export const MorningBriefDetailLevel = z.enum(['concise', 'balanced', 'detailed']);
// ---------------------------------------------------------------------------
// Read models
// ---------------------------------------------------------------------------
export const MorningBriefItem = z.object({
    id: z.string(),
    section: MorningBriefSection,
    itemType: MorningBriefItemType,
    entityType: nString,
    entityId: nString,
    title: z.string(),
    summary: nString,
    reason: nString,
    severity: MorningBriefSeverity,
    priority: nString,
    status: nString,
    suggestedAction: nString,
    sourceUrl: nString,
    reportUrl: nString,
    sortOrder: z.number().optional(),
    createdAt: isoDate,
});
/** Denormalized counts surfaced in the popup, drawer, and header badge. */
export const MorningBriefMetrics = z.object({
    important: z.number(),
    critical: z.number(),
    highlights: z.number(),
    lowlights: z.number(),
    mentionComments: z.number(),
    mentionDocuments: z.number(),
    assignedTickets: z.number(),
    watchedUpdates: z.number(),
});
export const MorningBrief = z.object({
    id: z.string(),
    userId: z.string(),
    briefDate: z.string(), // YYYY-MM-DD (user-local)
    timezone: nString,
    status: MorningBriefStatus,
    windowStartAt: nDate,
    windowEndAt: nDate,
    summary: nString,
    aiSource: nString, // llm | heuristic
    metrics: MorningBriefMetrics,
    importantCount: z.number(),
    criticalCount: z.number(),
    highlightCount: z.number(),
    lowlightCount: z.number(),
    mentionCommentCount: z.number(),
    mentionDocumentCount: z.number(),
    assignedTicketCount: z.number(),
    watchedUpdateCount: z.number(),
    firstPopupShownAt: nDate,
    readAt: nDate,
    dismissedAt: nDate,
    errorMessage: nString,
    generationCompletedAt: nDate,
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
/** Brief + its items, grouped by section. The full detail payload. */
export const MorningBriefDetail = MorningBrief.extend({
    items: z.array(MorningBriefItem),
});
/** Lightweight payload powering the header icon state (sub-500ms). */
export const MorningBriefIconState = z.object({
    state: z.enum(['none', 'unread', 'important', 'critical', 'failed', 'loading']),
    briefId: z.string().nullable(),
    badge: z.number(), // important count to show on the badge
    hasUnopened: z.boolean(),
});
// ---------------------------------------------------------------------------
// Preferences (per-user)
// ---------------------------------------------------------------------------
export const MorningBriefPreference = z.object({
    enabled: z.boolean(),
    showOnFirstLogin: z.boolean(),
    showTopRightIcon: z.boolean(),
    deliverHour: z.number(),
    timezone: nString,
    includeCommentMentions: z.boolean(),
    includeDocumentMentions: z.boolean(),
    includeAssignedTickets: z.boolean(),
    includeWatchedTickets: z.boolean(),
    includeSprintContext: z.boolean(),
    includeReleaseContext: z.boolean(),
    includePrContext: z.boolean(),
    includeTeamSummary: z.boolean(),
    emailDigestEnabled: z.boolean(),
    maxItemsPerSection: z.number(),
    detailLevel: MorningBriefDetailLevel,
});
export const UpdateMorningBriefPreferenceInput = z
    .object({
    enabled: z.boolean(),
    showOnFirstLogin: z.boolean(),
    showTopRightIcon: z.boolean(),
    deliverHour: z.number().int().min(0).max(23),
    timezone: z.string().nullable(),
    includeCommentMentions: z.boolean(),
    includeDocumentMentions: z.boolean(),
    includeAssignedTickets: z.boolean(),
    includeWatchedTickets: z.boolean(),
    includeSprintContext: z.boolean(),
    includeReleaseContext: z.boolean(),
    includePrContext: z.boolean(),
    includeTeamSummary: z.boolean(),
    emailDigestEnabled: z.boolean(),
    maxItemsPerSection: z.number().int().min(1).max(50),
    detailLevel: MorningBriefDetailLevel,
})
    .partial();
// ---------------------------------------------------------------------------
// Admin settings (singleton)
// ---------------------------------------------------------------------------
export const MorningBriefSetting = z.object({
    enabled: z.boolean(),
    defaultDeliverHour: z.number(),
    defaultTimezone: z.string(),
    maxItemsPerSection: z.number(),
    retentionDays: z.number(),
    firstLoginPopupEnabled: z.boolean(),
    topRightIconEnabled: z.boolean(),
    allowUserDisable: z.boolean(),
    allowManualRegenerate: z.boolean(),
    includeTeamSummary: z.boolean(),
    emailDigestEnabled: z.boolean(),
});
export const UpdateMorningBriefSettingInput = z
    .object({
    enabled: z.boolean(),
    defaultDeliverHour: z.number().int().min(0).max(23),
    defaultTimezone: z.string().min(1),
    maxItemsPerSection: z.number().int().min(1).max(50),
    retentionDays: z.number().int().min(1).max(365),
    firstLoginPopupEnabled: z.boolean(),
    topRightIconEnabled: z.boolean(),
    allowUserDisable: z.boolean(),
    allowManualRegenerate: z.boolean(),
    includeTeamSummary: z.boolean(),
    emailDigestEnabled: z.boolean(),
})
    .partial();
/**
 * Structured AI narrative the brief summarizer must return. The model receives
 * ONLY the already-assembled, permission-safe facts and narrates them — it never
 * queries data. Validated leniently (mirrors the other AI services) so a
 * malformed response degrades to the heuristic fallback rather than throwing.
 */
export const MorningBriefNarrative = z.object({
    summary: z.string(),
    highlights: z.array(z.string()).default([]),
    lowlights: z.array(z.string()).default([]),
    recommendedFocus: z.array(z.string()).default([]),
});
/** Admin generation-health rollup (Admin → Morning Brief). */
export const MorningBriefAdminStatus = z.object({
    date: z.string(),
    generated: z.number(),
    failed: z.number(),
    pending: z.number(),
    read: z.number(),
    dismissed: z.number(),
    llm: z.number(),
    heuristic: z.number(),
    optedInUsers: z.number(),
    recentFailures: z.array(z.object({ userId: z.string(), briefDate: z.string(), errorMessage: z.string().nullable() })),
});
//# sourceMappingURL=morning-brief.js.map